/**
 * One week, full width, directly under the chart it belongs to — the fix for a drilldown
 * that used to open a long way from the mark that prompted it.
 *
 * The five stats come from the CELL (the aggregate walk), the rows from the DRILL (a second
 * walk at operation grain over a different mart). They are meant to be two independent
 * derivations of the same week, so when the drill's reconciliation fails, this says so
 * loudly rather than showing rows that quietly do not add up (constitution rule 3).
 *
 * The panel is the LEVELLED decomposition in both views, which is what the design asks for:
 * "what is this week spent on" has one answer whichever encoding the chart above is drawn
 * in. Its subtitle says `levelled` so nobody reads the split as the as-scheduled one.
 */

import React from 'react'
import WeekDrillState from './WeekDrillState'
import WeekPanelHead from './WeekPanelHead'
import WeekSections from './WeekSections'
import WorksOrderPanel from './WorksOrderPanel'
import { useLoadingDrill } from '../../hooks/useLoading'
import type { LoadDrill } from '../../types/loading'
import type { ResourceModel, WeekModel } from '../../viewmodels/loading'
import { hrs, weekFull } from './format'

const HEAD_ROWS = 5
const ALL_ROWS = 500

/**
 * The week said out loud. FOUR cases, not the prototype's three.
 *
 * The prototype's sample data never produced an idle week — its bars are always full — so
 * `own === 0` could safely mean "the week is all catch-up". Real weeks go quiet: MILTURN4
 * w/c 19 Oct clears nothing, starts nothing and has all 240h free, and under the three-case
 * rule the panel announced that every hour of it went on work that was already late. The
 * emptiest week on the estate was described as the busiest.
 */
function verdict(w: WeekModel): string {
  if (w.clearing === 0 && w.own === 0) {
    return `Nothing is queued and nothing is due to start — the whole ${hrs(w.capacity)} is unsold.`
  }
  if (w.own === 0) {
    return 'Every hour this week goes on work that was already late — nothing of this week’s own demand starts.'
  }
  if (w.clearing === 0) {
    return `No backlog left to clear — the week runs on its own demand, with ${hrs(w.free)} unsold.`
  }
  const share = w.capacity ? Math.round((100 * w.clearing) / w.capacity) : 0
  return `${share}% of the week is spent catching up; only ${hrs(w.own)} of this week’s own work gets away.`
}

function Stats({ w }: { w: WeekModel }): React.JSX.Element {
  const cells: [string, number, string][] = [
    ['Capacity', w.capacity, 'var(--lc-ink)'],
    ['Spent clearing backlog', w.clearing, 'var(--lc-red)'],
    ['Due to start this week', w.own, 'var(--lc-teal)'],
    ['Free', w.free, w.free > 0 ? 'var(--lc-teal)' : 'var(--lc-mut)'],
    ['Still queued behind', w.carry, w.carry > 0 ? 'var(--lc-red)' : 'var(--lc-teal)'],
  ]
  return (
    <div className="grid grid-cols-2 gap-2.5 border-b px-4 py-3 md:grid-cols-3 xl:grid-cols-5" style={{ borderColor: 'var(--lc-b09)' }}>
      {cells.map(([k, v, color]) => (
        <div key={k} className="flex flex-col gap-0.5">
          <span className="font-sans text-[10.5px]" style={{ color: 'var(--lc-ink4)' }}>{k}</span>
          <span className="font-mono text-[17px] font-semibold tabular-nums tracking-[-.01em]" style={{ color }}>{hrs(v)}</span>
        </div>
      ))}
    </div>
  )
}

/** Constitution rule 3, on screen: two independent walks over two marts must agree, and
 *  when they do not the list must not be read as the week. */
function Unreconciled({ check }: { check: LoadDrill['sum_check'] }): React.JSX.Element {
  return (
    <p role="alert" className="border-b px-4 py-2 font-sans text-[11.5px]" style={{ background: 'var(--lc-red-bg)', borderColor: 'var(--lc-b09)', color: 'var(--lc-red-ink)' }}>
      The operations below sum to {hrs(check.rows_total)} against a cell of {hrs(check.cell_value)} —
      they do not reconcile, so read the list as incomplete.
    </p>
  )
}

function Rows({ drill, w, all, onAll, wo, onWo, detail }: {
  drill: LoadDrill
  w: WeekModel
  all: boolean
  onAll: () => void
  wo: string | null
  onWo: (wo: string) => void
  detail: (woKey: string) => React.ReactNode
}): React.JSX.Element {
  return (
    <>
      <WeekSections
        rows={drill.rows}
        clearing={w.clearing}
        own={w.own}
        limit={all ? ALL_ROWS : HEAD_ROWS}
        wo={wo}
        onWo={onWo}
        detail={detail}
      />
      <div className="px-4 pb-3.5">
        <button
          type="button"
          onClick={onAll}
          className="font-mono text-[10.5px] font-medium tracking-[.05em]"
          style={{ color: 'var(--lc-blue)' }}
        >
          {/* WORKS ORDERS, which is what the list shows and what the label says. It counted
              drill.rows — operations — so a week where one job ran twice on the same group
              promised more rows than it had: w/c 12 Oct offered 8 and holds 7. */}
          {all ? 'Show the heaviest few ↑' : `Open the full works-order list (${woCount(drill.rows)}) ↓`}
        </button>
      </div>
    </>
  )
}

/** Distinct works orders across both sections — a job with an operation in each is one
 *  job, and the button counts what the reader is about to be shown. */
const woCount = (rows: LoadDrill['rows']): number => new Set(rows.map((r) => r.wo_key)).size

/** The open works order's route, rendered as an expansion of the row that names it. */
const routeOf = (r: ResourceModel, onWo: (wo: string) => void) => (woKey: string) => (
  <WorksOrderPanel
    entity={r.entity}
    woKey={woKey}
    group={r.name}
    now={r.weeks[0].weekStart}
    weeks={r.weeks.length}
    onClose={() => onWo(woKey)}
  />
)

/** What this week amounts to, in one sentence. */
function Verdict({ w }: { w: WeekModel }): React.JSX.Element {
  return (
    <p
      className="border-b px-4 py-2.5 font-sans text-[11.5px] leading-[1.5]"
      style={{ background: 'var(--lc-verdict)', borderColor: 'var(--lc-b09)', color: 'var(--lc-ink3)' }}
    >
      {verdict(w)}
    </p>
  )
}

/** The window handed to the drill is `r.weeks.length` — the SERVED one, never a constant.
 *  The levelled walk the drill re-runs depends on the whole window, so asking for 13 weeks
 *  under a 52-week grid returns a bucket that does not reconcile to the cell that was
 *  clicked. Same reason WorksOrderPanel gets it. */
export default function WeekPanel({ r, week, onWeek, wo, onWo }: {
  r: ResourceModel
  week: number
  onWeek: (index: number) => void
  wo: string | null
  onWo: (wo: string) => void
}): React.JSX.Element {
  const [all, setAll] = React.useState(false)
  const w = r.weeks[week]
  const drill = useLoadingDrill(
    r.entity, r.name, w.weekStart, week === 0, 'levelled', r.weeks.length, r.basis
  )
  const failed = drill.data && !drill.data.sum_check.reconciles
  return (
    <div className="overflow-hidden rounded-[10px] border" style={{ borderColor: 'var(--lc-blue-edge)', background: 'var(--lc-week)' }}>
      <WeekPanelHead r={r} w={w} week={week} onWeek={onWeek} />
      <Stats w={w} />
      <Verdict w={w} />
      {failed && <Unreconciled check={drill.data!.sum_check} />}
      {drill.data ? (
        <Rows
          drill={drill.data}
          w={w}
          all={all}
          onAll={() => setAll((v) => !v)}
          wo={wo}
          onWo={onWo}
          detail={routeOf(r, onWo)}
        />
      ) : (
        <WeekDrillState
          error={drill.error ?? undefined}
          paused={drill.fetchStatus === 'paused'}
          onRetry={() => void drill.refetch()}
        />
      )}
    </div>
  )
}
